import { useLiveProfile, type DepthRung } from "../hooks/useLiveProfile.ts";

const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

// bid = blue family, ask = gold/amber. Each rung's bar is split: deeper "inner"
// segment = depth already counted at the tighter band (cumulative-so-far), and
// brighter "shell" segment = the increment added at THIS band. The increment
// NUMBER columns use the shell colors so number<->bar reinforce each other.
const BID_INNER = "#2f5bb0";
const BID_SHELL = "#6699ff";
const ASK_INNER = "#9a6a1e";
const ASK_SHELL = "#e0a458";

// brighter near-white for the highlighted anchor bands (50/100/200bps) — these
// match the ±0.5/1/2% compliance card bands, so they get a bolder/brighter
// treatment to stand out when scanning the fine ladder.
const TEXT_HI = "#eaf2ff";
const BID_SHELL_HI = "#9ec5ff";
const ASK_SHELL_HI = "#f0c585";

// bps bands that mirror the compliance card (±0.5 / 1 / 2%). Highlighted.
const ANCHOR_BANDS = new Set([50, 100, 200]);

// grid: bidCum | bidInc | bidBar | band | askBar | askInc | askCum
// Phone widths (src/mobile.css) narrow the fixed columns; desktop keeps these values.
const GRID = "var(--m-depth-cols, 64px 56px 1fr 64px 1fr 56px 64px)";

function fmtInc(n: number | null): string {
  if (n == null) return "—";
  const body = fmtK(Math.abs(n));
  if (n < 0) return "-" + body;   // should not occur (monotonic), but render honestly
  return "+" + body;
}

function fmtK(n: number | null): string {
  if (n == null) return "—";
  const a = Math.abs(n);
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (a >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return n.toFixed(0);
}

export function DepthLadder({
  venue,
  pair,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  const q = useLiveProfile(venue, pair);
  const prof = q.data;

  const bidRungs = prof?.bid_rungs ?? [];
  const askRungs = prof?.ask_rungs ?? [];

  const maxCum = Math.max(
    1,
    ...bidRungs.map((r) => r.cumulative_quote ?? 0),
    ...askRungs.map((r) => r.cumulative_quote ?? 0),
  );

  const quote = prof?.notional_unit ?? prof?.pair?.split("/")[1] ?? "";
  const ageStr =
    prof?.anchor_age_seconds == null ? "—" : `${prof.anchor_age_seconds}s`;

  return (
    <div
      style={{
        background: CARD_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding: "12px 14px",
        minWidth: "var(--m-depth-min, 520px)",
        flex: "1 1 560px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
          color: MUTED,
          marginBottom: 4,
        }}
      >
        <span>Depth profile · live book</span>
        <span>
          <span style={{ color: BID_SHELL }}>bid</span> ·{" "}
          <span style={{ color: ASK_SHELL }}>ask</span> · {quote} notional
        </span>
      </div>
      <div style={{ fontSize: 10, color: MUTED, marginBottom: 8 }}>
        bar: <span style={{ color: BID_INNER }}>▮</span>/<span style={{ color: ASK_INNER }}>▮</span> cumulative-so-far ·{" "}
        <span style={{ color: BID_SHELL }}>▮</span>/<span style={{ color: ASK_SHELL }}>▮</span> increment at this band · <span style={{ color: TEXT_HI }}>50/100/200bps emphasised</span> · anchor {ageStr}
      </div>

      {/* column header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: GRID,
          gap: 6,
          fontSize: 10,
          color: MUTED,
          marginBottom: 4,
          alignItems: "end",
        }}
      >
        <span style={{ textAlign: "right" }}>cumulative</span>
        <span style={{ textAlign: "right" }}>increment</span>
        <span />
        <span style={{ textAlign: "center" }}>band</span>
        <span />
        <span>increment</span>
        <span>cumulative</span>
      </div>

      {(prof ? prof.bid_rungs : []).map((_, i) => (
        <LadderRow
          key={i}
          band={bidRungs[i]?.label ?? askRungs[i]?.label ?? ""}
          bid={bidRungs[i]}
          ask={askRungs[i]}
          maxCum={maxCum}
        />
      ))}

      {prof && prof.status !== "ok" && (
        <div style={{ fontSize: 11, color: ASK_SHELL, marginTop: 8 }}>
          {prof.status === "no_anchor"
            ? "No fresh book anchor — cannot reconstruct the live book."
            : prof.status === "one_sided"
              ? "Book is one-sided; mid undefined, bands not measurable."
              : prof.status}
        </div>
      )}
      {q.isError && (
        <div style={{ fontSize: 11, color: ASK_SHELL, marginTop: 8 }}>could not load</div>
      )}
    </div>
  );
}

function SplitBar({
  cumulative,
  incremental,
  maxCum,
  innerColor,
  shellColor,
  align,
}: {
  cumulative: number | null;
  incremental: number | null;
  maxCum: number;
  innerColor: string;
  shellColor: string;
  align: "left" | "right";
}) {
  if (cumulative == null) {
    return <div style={{ height: 11 }} title="not measurable this book" />;
  }
  const inner = Math.max(0, cumulative - (incremental ?? 0));
  const innerPct = (inner / maxCum) * 100;
  const shellPct = ((incremental ?? 0) / maxCum) * 100;
  const dirRow: React.CSSProperties =
    align === "right" ? { justifyContent: "flex-end" } : { justifyContent: "flex-start" };
  // bid (right-align): shell is the OUTER/leftmost part growing toward center,
  // so order shell|inner. ask (left-align): inner|shell.
  const order = align === "right" ? ["shell", "inner"] : ["inner", "shell"];
  const seg = (kind: string) =>
    kind === "inner" ? (
      <div key="i" style={{ width: `${innerPct}%`, height: 11, background: innerColor }} />
    ) : (
      <div key="s" style={{ width: `${shellPct}%`, height: 11, background: shellColor }} />
    );
  return (
    <div
      style={{ display: "flex", ...dirRow, alignItems: "center" }}
      title={`cumulative ${cumulative.toFixed(0)} · +${(incremental ?? 0).toFixed(0)} at this band`}
    >
      {order.map(seg)}
    </div>
  );
}

function LadderRow({
  band,
  bid,
  ask,
  maxCum,
}: {
  band: string;
  bid?: DepthRung;
  ask?: DepthRung;
  maxCum: number;
}) {
  const label = band === "TOB" ? "top" : band.replace("bps", "");
  const bidMeas = bid?.measurable ?? false;
  const askMeas = ask?.measurable ?? false;

  // anchor bands (50/100/200bps) get a brighter + bolder treatment across the
  // whole row so they stand out against the finer rungs.
  const bps = bid?.bps ?? ask?.bps ?? -1;
  const hi = ANCHOR_BANDS.has(bps);
  const weight = hi ? 700 : 400;
  const cumColor = (meas: boolean) => (meas ? (hi ? TEXT_HI : TEXT) : MUTED);
  const bidIncColor = bidMeas ? (hi ? BID_SHELL_HI : BID_SHELL) : MUTED;
  const askIncColor = askMeas ? (hi ? ASK_SHELL_HI : ASK_SHELL) : MUTED;
  const labelColor = hi ? TEXT_HI : MUTED;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: GRID,
        gap: 6,
        alignItems: "center",
        marginBottom: 5,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {/* bid cumulative */}
      <span style={{ fontSize: 11, fontWeight: weight, color: cumColor(bidMeas), textAlign: "right" }}>
        {fmtK(bid?.cumulative_quote ?? null)}
      </span>
      {/* bid increment (shell color) */}
      <span style={{ fontSize: 11, fontWeight: weight, color: bidIncColor, textAlign: "right" }}>
        {bidMeas ? fmtInc(bid?.incremental_quote ?? 0) : "—"}
      </span>
      {/* bid bar (grows toward center) */}
      <SplitBar
        cumulative={bid?.cumulative_quote ?? null}
        incremental={bid?.incremental_quote ?? null}
        maxCum={maxCum}
        innerColor={BID_INNER}
        shellColor={BID_SHELL}
        align="right"
      />
      {/* band */}
      <span style={{ fontSize: 11, fontWeight: weight, color: labelColor, textAlign: "center" }}>
        {label}{band === "TOB" ? "" : "bps"}
      </span>
      {/* ask bar (grows away from center) */}
      <SplitBar
        cumulative={ask?.cumulative_quote ?? null}
        incremental={ask?.incremental_quote ?? null}
        maxCum={maxCum}
        innerColor={ASK_INNER}
        shellColor={ASK_SHELL}
        align="left"
      />
      {/* ask increment (shell color) */}
      <span style={{ fontSize: 11, fontWeight: weight, color: askIncColor }}>
        {askMeas ? fmtInc(ask?.incremental_quote ?? 0) : "—"}
      </span>
      {/* ask cumulative */}
      <span style={{ fontSize: 11, fontWeight: weight, color: cumColor(askMeas) }}>
        {fmtK(ask?.cumulative_quote ?? null)}
      </span>
    </div>
  );
}
